import axios from 'axios'
import * as Sentry from '@sentry/vue'

const TIMEOUT = 60000; // 60 seconds

const httpClient = axios.create({
  baseURL: '/',
  timeout: TIMEOUT,
  headers: {
    // This is just a default that can be overridden
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Shared promise to prevent concurrent CSRF token refreshes
let csrfTokenRefreshPromise = null

// NOTE: This CSRF handling is also implemented in application.js as part of the root Vue app
// but this httpClient pattern should be used going forward and eventually replace it
httpClient.interceptors.request.use(
  config => {
    // Add CSRF token to the request
    // Use safe navigation because test envs don't have a CSRF token by default
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    config.headers['X-CSRF-Token'] = token

    // If we're sending a FormData object, we need to set the content type to multipart/form-data
    if (config.data instanceof FormData) {
      // This overwrites the default application/json content type set above
      config.headers['Content-Type'] = 'multipart/form-data'
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle CSRF token errors with automatic retry
httpClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    // Check if this is a CSRF token error and we haven't already retried
    // Rails returns 422 for InvalidAuthenticityToken exceptions
    const is422Error = error.response?.status === 422
    const errorData = error.response?.data
    
    // Extract error message safely, handling circular references
    let errorMessage = ''
    if (typeof errorData === 'string') {
      errorMessage = errorData
    } else if (errorData?.message) {
      errorMessage = errorData.message
    } else if (errorData) {
      try {
        errorMessage = JSON.stringify(errorData)
      } catch {
        errorMessage = ''
      }
    }
    
    const isCSRFError = errorMessage.toLowerCase().includes('authenticity') || 
                        errorMessage.toLowerCase().includes('csrf') ||
                        errorMessage.includes('InvalidAuthenticityToken')
    
    // Attempt to recover from CSRF errors by fetching a fresh token
    // If recovery fails, fall through to reject the original error
    if (is422Error && isCSRFError && !originalRequest._retry) {
      originalRequest._retry = true

      // If a token refresh is already in progress, wait for it
      // This prevents multiple concurrent requests from fetching the token simultaneously
      if (!csrfTokenRefreshPromise) {
        csrfTokenRefreshPromise = (async () => {
          try {
            // Fetch the root page to get a fresh CSRF token
            const response = await fetch('/', {
              credentials: 'same-origin',
              headers: { 'Accept': 'text/html' }
            })
            
            if (response.ok) {
              const html = await response.text()
              const parser = new DOMParser()
              const doc = parser.parseFromString(html, 'text/html')
              const newToken = doc.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
              
              if (newToken) {
                // Update the token in the DOM for future requests
                const tokenMeta = document.querySelector('meta[name="csrf-token"]')
                if (tokenMeta) {
                  tokenMeta.setAttribute('content', newToken)
                }
                return newToken
              }
            }
            return null
          } catch (refreshError) {
            console.error('CSRF token refresh failed:', refreshError)
            Sentry.captureException(refreshError, {
              tags: { component: 'csrf-token-refresh' },
              extra: { originalRequestUrl: originalRequest.url }
            })
            return null
          } finally {
            // Clear the promise so future requests can retry if needed
            csrfTokenRefreshPromise = null
          }
        })()
      }

      const newToken = await csrfTokenRefreshPromise
      
      if (newToken) {
        // Retry the original request with the new token
        originalRequest.headers['X-CSRF-Token'] = newToken
        return httpClient(originalRequest)
      }
    }

    return Promise.reject(error)
  }
)

export default httpClient
